import * as React from 'react';

import FPS, { FpsSnapshot } from '../../../utils/fps';
import './index.scss';

interface IFpsProps {
  fps: FPS;
}

const Fps = (props: IFpsProps) => {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const [snapshot, setSnapshot] = React.useState<FpsSnapshot>(props.fps.snapshot());

  React.useEffect(() => {
    const onUpdate = () => {
      setSnapshot(props.fps.snapshot());
    };
    props.fps.on('update', onUpdate);
    return () => {
      props.fps.off('update', onUpdate);
    };
  }, [props.fps]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const width = canvas.width;
    const height = canvas.height;
    const paddingTop = 20;
    const chartHeight = height - paddingTop;
    const maxFrameTime = 50;
    const frameTimes = snapshot.frameTimes;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#f5f9fd';
    ctx.fillRect(0, 0, width, height);

    const drawGuideLine = (ms: number, color: string) => {
      const y = paddingTop + (Math.min(ms, maxFrameTime) / maxFrameTime) * chartHeight;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    };

    drawGuideLine(16.7, 'rgba(79, 123, 164, 0.45)');
    drawGuideLine(33.3, 'rgba(201, 129, 94, 0.35)');

    if (frameTimes.length < 2) {
      return;
    }

    const stepX = width / Math.max(1, frameTimes.length - 1);
    ctx.beginPath();
    for (let i = 0; i < frameTimes.length; i++) {
      const x = i * stepX;
      const clamped = Math.min(frameTimes[i], maxFrameTime);
      const y = paddingTop + (clamped / maxFrameTime) * chartHeight;
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.strokeStyle = '#2f6fa8';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    const jankPercent =
      snapshot.totalFrames > 0
        ? Math.round((snapshot.slowFrameCount / snapshot.totalFrames) * 100)
        : 0;
    const fpsText = `FPS ${snapshot.latest}`;
    const p95Text = `P95 ${snapshot.p95FrameTime}ms`;
    const jankText = `Jank ${jankPercent}%`;
    const leftPadding = 6;
    const rightPadding = 6;
    const gap = 8;

    ctx.fillStyle = '#27445d';
    ctx.font = '11px sans-serif';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    ctx.fillText(fpsText, leftPadding, 4);

    ctx.textAlign = 'right';
    ctx.fillText(jankText, width - rightPadding, 4);

    const fpsWidth = ctx.measureText(fpsText).width;
    const p95Width = ctx.measureText(p95Text).width;
    const jankWidth = ctx.measureText(jankText).width;
    const p95MinX = leftPadding + fpsWidth + gap;
    const p95MaxX = width - rightPadding - jankWidth - gap - p95Width;
    const centeredP95X = (width - p95Width) / 2;
    const p95X = Math.max(p95MinX, Math.min(centeredP95X, p95MaxX));

    if (p95MaxX >= p95MinX) {
      ctx.textAlign = 'left';
      ctx.fillText(p95Text, p95X, 4);
    }
  }, [snapshot]);

  return (
    <section className="fps" aria-label="FPS Performance Panel">
      <canvas ref={canvasRef} className="fps-chart" width={220} height={72} />
    </section>
  );
};

export default React.memo(Fps);
